import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify"; // Import ToastContainer
import "react-toastify/dist/ReactToastify.css";
import CustomToast from "./CustomToast"; // Import CustomToast
import ProductDetails from "./ProductDetails";
import ProductImageCarousel from "./ProductImageCarousel";
import RelatedProducts from "./RelatedProducts";
import ReviewSection from "./ReviewSection";
import AuthModal from "./AuthModal";
import { getCartItems } from "../api/Product.services";
import { fetchWithAuth } from "../api/AuthApi.services";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ProductDetailsPage.css";
import AddressSelectionModal from "./AddressSelectionModal"; // For address selection

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const ProductDetailsPage = ({ setCartItemsCount }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [thumbnails, setThumbnails] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [comments, setComments] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false); // Add loading state
  const accessToken = localStorage.getItem("accessToken");
  const [showAddressModal, setShowAddressModal] = useState(false); // Added
  const [addressList, setAddressList] = useState([]);
  const [loginIntent, setLoginIntent] = useState(null); // Track why login was triggered (e.g., "buyNow", "comment")

  const handleCartSuccess = async (message, isCartCount = true) => {
    toast(<CustomToast message={`1 product`} />, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
    });
    if (isCartCount) {
      const response = await getCartItems();
      setCartItemsCount(response[0]?.items?.length || 0);
    }
  };

  const handleCartError = (message) => {
    toast.error(message, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const handleLoginClick = () => {
    setLoginIntent("comment"); // Set intent to "comment"
    setShowAuthModal(true);
  };

  const handleAddToCart = async (productId) => {
    setIsAddingToCart(true);
    try {
      const guest_user_id = localStorage.getItem("guest_user_id");
      const accessToken = localStorage.getItem("accessToken");

      const headers = {
        "Content-Type": "application/json",
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const body = {
        product: productId,
      };

      if (guest_user_id) {
        body.guest_user_id = guest_user_id;
      }

      const response = accessToken
        ? await fetchWithAuth(
          `${API_BASE_URL}/order/carts/add-item/`,
          {
            method: "POST",
            headers: headers,
            body: JSON.stringify(body),
          }
        )
        : await fetch(
          `${API_BASE_URL}/order/carts/add-item/`,
          {
            method: "POST",
            headers: headers,
            body: JSON.stringify(body),
          }
        );

      const data = await response.json();

      if (response.ok) {
        if (data.guest_user_id) {
          localStorage.setItem("guest_user_id", data.guest_user_id);
        }
        handleCartSuccess(`${product.name} added to cart!`, true);
      } else {
        const errorMessage = data.error || "Failed to add to cart";
        handleCartError(errorMessage);
      }
    } catch (error) {
      handleCartError("Failed to add to cart.");
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      setLoginIntent("buyNow"); // Set intent to "buyNow"
      setShowAuthModal(true); // Show login modal if not logged in
      return;
    }

    try {
      const addresses = await fetchAddresses(accessToken);
      if (!addresses) return;

      setAddressList(addresses);
      setShowAddressModal(true); // Open address selection modal
    } catch (error) {
      console.error("Error during Buy Now:", error);
      handleCartError("Error during Buy Now process.");
    }
  };

  const fetchAddresses = async (token) => {
    const response = await fetch(`${API_BASE_URL}/account/addresses/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return await response.json();
    } else if (response.status === 401) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        const refreshResponse = await fetch(`${API_BASE_URL}/account/token/refresh/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: refreshToken }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          localStorage.setItem("accessToken", refreshData.access);
          return fetchAddresses(refreshData.access); // Retry with new token
        } else {
          handleCartError("Session expired. Please log in again.");
          setShowAuthModal(true);
          return null;
        }
      } else {
        setShowAuthModal(true);
        return null;
      }
    } else {
      handleCartError("Failed to fetch addresses.");
      return null;
    }
  };

  const getAddressData = async () => {
    if(loginIntent === "buyNow" ){
      handleBuyNow();
      setShowAuthModal(false); 
    }else {
      setShowAuthModal(false);
    }
  };

  const handleAddressSelect = (addressId) => {
    if (!addressId) {
      handleCartError("Please select an address.");
      return;
    }
    createOrder(addressId);
  };

  const createOrder = async (addressId) => {
    const accessToken = localStorage.getItem("accessToken");
    try {
      const orderResponse = await fetchWithAuth(
        `${API_BASE_URL}/order/orders/create_order/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            final_amount: product.price, // Use single product price
            notes: { description: `Order for ${product.name}` },
            delivery_address: addressId,
            items: [{ product: product.id, quantity: 1 }], // Single product order
          }),
        }
      );

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        handleCartError(errorData.error || "Failed to create order.");
        return;
      }

      const orderData = await orderResponse.json();
      initiatePayment(orderData);
    } catch (error) {
      console.error("Error creating order:", error);
      handleCartError("Failed to initiate checkout.");
    }
  };

  const initiatePayment = (orderData) => {
    const { razorpay_order_id, email, username, contact } = orderData;

    const options = {
      key: "rzp_live_RQkuUEQd2IpGA5", // Replace with your Razorpay key
      amount: product.price * 100, // Convert to paise (Razorpay expects amount in smallest unit)
      currency: "INR",
      name: "VastuAura Aligners",
      description: `Payment for ${product.name}`,
      order_id: razorpay_order_id,
      handler: async function (response) {
        handleCartSuccess(`Payment successful! Payment ID: ${response.razorpay_payment_id}`, false);
        await recordPayment(response);
      },
      prefill: { name: username, email: email, contact: contact },
      theme: { color: "#3399cc" },
    };

    const razorpayInstance = new window.Razorpay(options);
    razorpayInstance.open();

    razorpayInstance.on("payment.failed", function (response) {
      console.error("Payment Failure Response:", response.error);
      handleCartError("Payment failed. Please try again.");
      recordPayment(response, "FAILED");
    });
  };

  const recordPayment = async (response, status = "COMPLETED") => {
    const accessToken = localStorage.getItem("accessToken");
    try {
      const paymentResponse = await fetchWithAuth(
        `${API_BASE_URL}/payment/create_payment/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            amount: product.price,
            payment_status: status,
            from: "product_detail",
          }),
        }
      );

      if (!paymentResponse.ok) {
        handleCartError("Failed to record payment. Please contact support.");
      } else {
        handleCartSuccess("Payment recorded successfully!", false);
        navigate("/orders"); // Redirect to order details page
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      handleCartError("Payment recording failed.");
    }
  };

  const handleLoginSuccess = () => {
    const newAccessToken = localStorage.getItem("accessToken");
    if (newAccessToken) {
      setShowAuthModal(false);
      if (loginIntent === "buyNow") {
        handleBuyNow(); // Trigger Buy Now only if login was for "Buy Now"
      } else if (loginIntent === "comment") {
        fetchComments(); // Refresh comments if login was for commenting
      }
      setLoginIntent(null); // Reset intent after handling
    }
  };

  const fetchComments = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/product/${productId}/comments/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }
      const data = await response.json();
      setComments(data.results || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  }, [productId]);

  useEffect(() => {
    async function fetchProductDetails() {
      try {
        const response = await fetch(
          `${API_BASE_URL}/product/products/${productId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch product details");
        }
        const data = await response.json();

        const formattedProduct = {
          id: data.id,
          name: data.name || "Unknown Product",
          discount: `${data.discount || 0}%`,
          price: parseFloat(data.actual_unit_price) || 0,
          originalPrice: parseFloat(data.unit_price) || 0,
          offerCode: "NAMASTE",
          offerDetails: "Free 5 Mukhi Rudraksha worth ₹399",
          additionalInfo: "Get Siddh/Energized Product (Pran Pratistha) + ₹100",
          codButtonText: "ADD TO CART",
          onlinePayButtonText: "BUY NOW",
          category: data.category?.name || "",
          subCategory: data.sub_category?.[0]?.name || "",
          labels: data.label ? data.label.map((label) => ({
            name: label.name,
            description: label.description,
          })) : [],
          tags: data.tags ? data.tags.map((tag) => ({
            name: tag.name,
            description: tag.description,
          })) : [],
          ratingsCount: data.ratings_count || 0, // Add ratings_count
          averageRating: Math.round(data.average_rating) || 0,
          discription: data?.description || "No description available"
        };

        setProduct(formattedProduct);
        setImages(data.documents?.map((doc) => doc.picture) || []);
        setThumbnails(data.documents?.map((doc) => doc.picture) || []);

        fetchRelatedProducts(formattedProduct.category, formattedProduct.subCategory, data.id);
        fetchComments();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    if (productId) {
      fetchProductDetails();
    }
  }, [productId, fetchComments]);

  async function fetchRelatedProducts(category, subCategory, currentProductId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/product/products/?category=${category}&sub_category=${subCategory}&priority=high`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch related products");
      }
      const data = await response.json();

      const formattedProducts = (data.results || [])
        .filter((p) => p.id !== currentProductId)
        .map((product) => ({
          id: product.id,
          name: product.name,
          image: product?.picture ? product?.picture : "https://via.placeholder.com/300",
          tagline: product.label?.length > 0 ? product.label[0].name : "",
          currentPrice: parseFloat(product.unit_price) - parseFloat(product.discount),
          originalPrice: parseFloat(product.unit_price),
          discount: product.discount,
          labels: product.tags ? product.tags.map((tag) => tag.name) : [],
        }));

      setRelatedProducts(formattedProducts);
    } catch (error) {
      console.error("Error fetching related products:", error);
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const isYesterday =
      date.getDate() === now.getDate() - 1 &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      return "Today";
    } else if (isYesterday) {
      return "Yesterday";
    } else {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    }
  };

  if (loading) return <div className="container mt-5"><p>Loading...</p></div>;
  if (error) return <div className="container mt-5"><p>{error}</p></div>;

  return (
    <div className="product-details-container">
      <ToastContainer /> {/* Add ToastContainer */}
      <div className="row justify-content-center align-self-center m-0 mb-3">

        <div className="col-lg-5 sticky-cart-img">
          <ProductImageCarousel images={images} thumbnails={thumbnails} />
          <div className="cart-btns" style={{ marginTop: "20px", marginBottom: "20px" }}>
            <div className="d-flex gap-2 justify-content-center align-items-center">
              <button className="btn btn-orange" onClick={() => handleAddToCart(product?.id)} disabled={isAddingToCart} >
                {isAddingToCart ? "Adding..." : "🛒 Add to Cart"}
              </button>
              <button className="btn btn-dark"onClick={handleBuyNow}>
                ⚡ Buy Now
              </button>
            </div>
          </div>
        </div>
        <div className="col-lg-7">
          <div className="row justify-content-center align-self-center mb-3">
            <div className="col-sm-11 col-12">
              <ProductDetails product={product} />
            </div>
          </div>
          <div className="row justify-content-center align-self-center mb-3">
            <div className="col-sm-11 col-12" style={{ border: "1px solid #f0f0f0", borderRadius: "12px" }}>
              <ReviewSection
                productId={productId}
                accessToken={accessToken}
                fetchComments={fetchComments}
                comments={comments}
                formatDate={formatDate}
                handleLoginClick={handleLoginClick}
                onCartSuccess={handleCartSuccess}
                onCartError={handleCartError}
              />
            </div>
          </div>
        </div>
      </div>

      <div id="related_Product" className="row justify-content-center align-self-center m-0 mb-3" >
        <div className="col-12" style={{ border: "1px solid #f0f0f0", borderRadius: "12px" }}>
          <RelatedProducts
            relatedProducts={relatedProducts}
            onCartSuccess={handleCartSuccess}
            onCartError={handleCartError}
          />
        </div>
      </div>

      <AuthModal
        show={showAuthModal}
        onClose={getAddressData}
        onCloseModel={()=>{setShowAuthModal(false)}}
        onLoginSuccess={handleLoginSuccess}
        redirectFrom = {"cartPage"}
      />
      {showAddressModal && (
        <AddressSelectionModal
          addresses={addressList}
          onSelect={handleAddressSelect}
          onClose={() => setShowAddressModal(false)}
        />
      )}
    </div>
  );
};

export default ProductDetailsPage;